/* eslint-disable no-unused-vars */
import { memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { manageCampaignsActions } from 'store/actions';
import SettingsBtn from 'assets/svgs/SettingsBtn';
import { IconButton, Buttons } from 'components';
import { isAiAssistantEnabled } from 'utils';
import { isAdminSelector, isChatWidgetAvailableSelector } from 'store/selectors';
import { routes } from 'config';
import { useNavigate, Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import * as S from './ManageCampaignHeader.styled';
import { ChartIcon, Plus, AdvancedSettings } from 'assets/icons';
import AppnextAiIcon from 'assets/icons/Appnext_AI_Icon.png';
import { AppsMonthlyBudgets } from './appsMonthlyBudgets/AppsMonthlyBudgets';
import { ManageCampaignCalendar } from './manageCampaignCalendar/ManageCampaignCalendar';

const AiAssistantIcon = () => <img src={AppnextAiIcon} alt='AI Assistant' />;

export const ManageCampaignsHeader = memo(({ isDisabledShowChartBtn, timePeriod }) => {
  const numOfDays = timePeriod ? getNumOfDays(timePeriod.from, timePeriod.to) : 0;
  const isChartHidden = useSelector((state) => state.manageCampaigns.isChartHidden);
  const isAdmin = useSelector(isAdminSelector);
  const isWidgetAvailable = useSelector(isChatWidgetAvailableSelector);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const goToCreateCampaign = () => {
    let url = routes.list.find((route) => route.id === 'campaign/newCampaign_empty').url;
    navigate(url);
  };

  const goToCampaignsSettings = () => {
    let url = routes.list.find((route) => route.id === 'campaign/campaignsSettings').url;
    navigate(url);
  };

  const handleAiAssistantClick = useCallback(() => {
    // Try the widget API first
    if (window.AppnextChat && typeof window.AppnextChat.open === 'function') {
      window.AppnextChat.open();
      return;
    }
    // Fallback: temporarily show the toggle, click it, then re-hide
    const toggle = document.querySelector('.cai-widget-toggle');
    if (toggle) {
      toggle.style.setProperty('display', '', '');
      toggle.click();
      toggle.style.setProperty('display', 'none', 'important');
    }
  }, []);

  return (
    <S.Header>
      <S.Title>Manage Campaigns</S.Title>
      <S.Box gap={8}>
        <ManageCampaignCalendar />
        <IconButton.NormalSize
          disabled={isDisabledShowChartBtn || numOfDays > 180 || numOfDays < 2}
          icon={ChartIcon}
          isPressed={!isChartHidden}
          type={IconButton.btnTypes.tertiary}
          onClick={() => {
            dispatch(
              manageCampaignsActions.updateProperty({
                propField: 'isChartHidden',
                newValue: !isChartHidden,
              }),
            );
          }}
        />
      </S.Box>
      <S.Box gap={16}>
        <AppsMonthlyBudgets />
        <Buttons.TertiaryNormal
          icon={<AdvancedSettings />}
          id='save_start_campaign'
          onClick={goToCampaignsSettings}
        >
          Settings
        </Buttons.TertiaryNormal>
        <S.CreateCampaignGroup>
          <Buttons.PrimaryNormal
            icon={<Plus />}
            id='createCampaign'
            onClick={() => goToCreateCampaign()}
            style={
              isAiAssistantEnabled(isAdmin, isWidgetAvailable)
                ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                : undefined
            }
          >
            Create Campaign
          </Buttons.PrimaryNormal>
          {isAiAssistantEnabled(isAdmin, isWidgetAvailable) && (
            <Tooltip title='Start with AI Assistant' arrow placement='top'>
              <S.AiAssistantButton onClick={handleAiAssistantClick}>
                <AiAssistantIcon />
              </S.AiAssistantButton>
            </Tooltip>
          )}
        </S.CreateCampaignGroup>
      </S.Box>
    </S.Header>
  );
});

function getNumOfDays(from, to) {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return Math.floor((toDate - fromDate) / (24 * 60 * 60 * 1000)) + 1;
}

ManageCampaignsHeader.displayName = 'ManageCampaignsHeader';
